import { ApplicationConfig, LOCALE_ID, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { InMemoryScrollingFeature, InMemoryScrollingOptions, provideRouter, withInMemoryScrolling } from '@angular/router';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

/*
 * Import the routes from the app.routes file
 **/
import { DATE_PIPE_DEFAULT_OPTIONS, DatePipe, registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { localization } from '@config/localization.config';
import { mainConfig } from '@config/main.config';
import { caricamentoInterceptor } from '@services/caricamento.interceptor';
import { tokenInterceptor } from '@services/token-interceptor.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { paginatoreItaliano } from '@config/paginator-italiano.config';
import { NgxUploadModule } from '@sinapsys/ngx-upload';
import { routes } from './app.routes';
import { QuillModule } from 'ngx-quill';

/*
 * Import the HttpClient and TranslateLoader classes
 **/
interface I18NConfig {
  defaultLanguage: string;
  loader: {
    provide: typeof TranslateLoader;
    useFactory: (http: HttpClient) => TranslateHttpLoader;
    deps: (typeof HttpClient)[];
  };
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/public/i18n/', '.json');
}

export const I18N_CONFIG: I18NConfig = {
  defaultLanguage: localization.defaultLanguage,
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient],
  },
};

/**
 * Dati della lingua italiana.
 *
 * `LOCALE_ID` da solo non basta: senza registrare i dati della lingua, ogni
 * `| number` e `| date` fallisce con "NG0701: Missing locale data for the
 * locale it". Nell'avviso sulle dimensioni della copertina il conteggio dei
 * pixel non e' mai comparso: si leggeva "(  pixel totali)".
 */
registerLocaleData(localeIt);

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature = withInMemoryScrolling(scrollConfig);

/*
 * Define the application configuration
 **/
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, inMemoryScrollingFeature),
    provideHttpClient(withInterceptors([tokenInterceptor, caricamentoInterceptor])),
    provideAnimationsAsync(),
    importProvidersFrom(TranslateModule.forRoot(I18N_CONFIG)),
    importProvidersFrom(
      NgxUploadModule.forRoot({
        mediaBaseUrl: mainConfig.mediaBaseUrl,
      }),
      QuillModule.forRoot({
        suppressGlobalRegisterWarning: true
      }),
    ),
    DatePipe,
    { provide: 'COMMON_APP_CONFIG', useValue: mainConfig },
    // Campi a contorno invece che a riquadro pieno: e' la forma che vuole il
    // design (bordi netti, niente blocchi colorati) e toglie di mezzo il fondo
    // rosato che la tavolozza generata dal rosso dava ai campi.
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' },
    },
    { provide: MatPaginatorIntl, useFactory: paginatoreItaliano },
    {
      provide: LOCALE_ID,
      useValue: mainConfig.localization.defaultLocale,
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: {
        dateFormat: mainConfig.dateFormat,
      },
    },
  ],
};
